"use client";

import { Fragment, useState, useEffect, useRef, useCallback } from "react";
import Link from "@/components/i18n/link";
import { useFiltersStore } from "@/lib/filters-store";
import { Search, SlidersHorizontal, X, ChevronDown, Loader2, ArrowRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { EventCard } from "@/components/EventCard";
import { CiudadVivaEvent, categories } from "@/lib/events";
import { fetchEvents, fetchDistricts, fetchOrganizations, type DistrictOption } from "@/lib/api";
import { countryName } from "@/lib/countries";
import { useCountry } from "@/lib/country-store";
import { DEFAULT_COUNTRY } from "@/lib/event-countries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { recordComunaFilter } from "@/lib/newsletter-soft-ask";
import { triggerNewsletterSoftAsk } from "@/components/NewsletterSoftAsk";
import { PAGE_SIZE } from "./constants";
import { useLocale, useMessages } from "@/lib/i18n/client";
import eventMessages from "@/lib/events.i18n";
import messages from "./EventosClient.i18n";
import { DailyWordInvite } from "@/components/daily-word-invite";

// Tras cuántas tarjetas aparece la invitación a "La palabra del día".
const GAME_INVITE_AFTER = 6;
import { track } from "@/lib/tracking/track";

type T = ReturnType<typeof useMessages<(typeof messages)["es"]>>;

function useDebounce<T>(value: T, delay = 350): [T, (v: T) => void] {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return [debounced, setDebounced];
}

interface Props {
  initialEvents: CiudadVivaEvent[];
  initialTotal: number;
  initialTotalPages: number;
}

export function EventosClient({ initialEvents, initialTotal, initialTotalPages }: Props) {
  const {
    when, setWhen,
    price, setPrice,
    category: cat, setCategory: setCat,
    districts, setDistricts,
    organizationIds, setOrganizationIds,
    search: q, setSearch: setQ,
    bootstrapDone,
  } = useFiltersStore();

  const [debouncedQ] = useDebounce(q);
  const [mobileOpen, setMobileOpen] = useState(false);
  const t = useMessages(messages);
  const tEvents = useMessages(eventMessages);
  const lang = useLocale();
  const { country, loaded: countryLoaded } = useCountry();

  // El modal de filtros es un overlay fixed, pero por sí solo no impide que
  // el contenido de atrás se siga scrolleando (scroll chaining desde el
  // panel o directamente sobre el fondo oscuro). Bloqueamos el body mientras
  // está abierto.
  useEffect(() => {
    if (!mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  // Señal de interés proxy para el soft-ask del newsletter: si el usuario
  // filtra 2 veces por la misma comuna, se dispara el toast contextual.
  // NOTA: `districts` y `bootstrapDone` se setean en el mismo batch cuando
  // el filtro viene hidratado desde la URL (?districts=...), así que ese
  // primer valor SÍ cuenta como una interacción — es una señal proxy, no
  // necesita distinguir origen (URL vs. click real del usuario).
  useEffect(() => {
    if (!bootstrapDone) return;
    if (districts.length !== 1) return;
    const comunaCount = recordComunaFilter(districts[0]);
    if (comunaCount === 2) triggerNewsletterSoftAsk();
  }, [bootstrapDone, districts]);

  // Analítica: búsqueda (ya con debounce) y cada filtro que cambia. Lo que
  // llega hidratado desde la URL no cuenta (no es una acción hecha acá; la
  // búsqueda del Hero ya se registró allá). Por eso la primera foto usa `q`
  // y no `debouncedQ`, que todavía viene atrasado.
  const trackedFilters = useRef<Record<string, string> | null>(null);
  useEffect(() => {
    if (!bootstrapDone) return;
    const prev = trackedFilters.current;
    const now = {
      q: (prev ? debouncedQ : q).trim(),
      category: cat ?? "",
      district: districts.join(","),
      organization: organizationIds.join(","),
      date: when === "all" ? "" : when,
      price: price === "all" ? "" : price,
    };
    trackedFilters.current = now;
    if (!prev) return;
    if (now.q && now.q !== prev.q) track("search", { search_term: now.q, source: "events" });
    for (const type of ["category", "district", "organization", "date", "price"] as const) {
      if (now[type] && now[type] !== prev[type]) {
        track("filter_apply", { filter_type: type, filter_value: now[type].slice(0, 100) });
      }
    }
    // `q` sin debounce solo se lee para la primera foto (no como disparador).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrapDone, debouncedQ, cat, districts, organizationIds, when, price]);

  // El top del sidebar sticky se mide del alto real de la barra de búsqueda
  // sticky (header + input + chips) en vez de un valor fijo — un número
  // hardcodeado que no calza exacto con esa altura hace que el sidebar
  // "salte" (pierda su margen superior) justo cuando el scroll activa el
  // sticky, porque el gap visible antes de engancharse (el py-10 del grid)
  // no coincide con el gap una vez enganchado.
  const searchBarRef = useRef<HTMLDivElement>(null);
  const [sidebarTop, setSidebarTop] = useState(216);

  useEffect(() => {
    function measure() {
      if (searchBarRef.current) {
        setSidebarTop(searchBarRef.current.getBoundingClientRect().bottom + 40);
      }
    }
    measure();
    window.addEventListener("resize", measure);
    document.fonts?.ready?.then(measure).catch(() => {});
    return () => window.removeEventListener("resize", measure);
  }, []);

  const [districtOptions, setDistrictOptions] = useState<DistrictOption[]>([]);
  const [districtsError, setDistrictsError] = useState(false);
  const [organizations, setOrganizations] = useState<{ _id: string; name: string; eventCount: number }[]>([]);
  const [organizationsError, setOrganizationsError] = useState(false);

  const isMounted = useRef(false);

  // Al resolverse el país (y cada vez que se cambia en el selector global):
  // opciones de ubicación y organización de ese país, descartando las
  // elegidas que no le corresponden.
  useEffect(() => {
    if (!countryLoaded) return;
    fetchDistricts(undefined, { country })
      .then((data) => {
        setDistrictOptions(data);
        setDistrictsError(false);
        const valid = new Set(data.map((d) => d.name));
        const current = useFiltersStore.getState().districts;
        const pruned = current.filter((d) => valid.has(d));
        if (pruned.length !== current.length) setDistricts(pruned);
      })
      .catch(() => setDistrictsError(true));
    fetchOrganizations(undefined, { country })
      .then((data) => {
        setOrganizations(data);
        setOrganizationsError(false);
        const valid = new Set(data.map((o) => o._id));
        const current = useFiltersStore.getState().organizationIds;
        const pruned = current.filter((id) => valid.has(id));
        if (pruned.length !== current.length) setOrganizationIds(pruned);
      })
      .catch(() => setOrganizationsError(true));
    isMounted.current = true;
    // setDistricts/setOrganizationIds son acciones del store (referencia estable).
  }, [country, countryLoaded, setDistricts, setOrganizationIds]);

  useEffect(() => {
    if (!isMounted.current) return;
    fetchOrganizations(districts.length ? districts : undefined, { country })
      .then((data) => {
        setOrganizations(data);
        setOrganizationsError(false);
        const valid = new Set(data.map((o) => o._id));
        const pruned = organizationIds.filter((id) => valid.has(id));
        if (pruned.length !== organizationIds.length) setOrganizationIds(pruned);
      })
      .catch(() => setOrganizationsError(true));
    // Solo cuando cambian las comunas: incluir organizationIds haría que cada
    // poda vuelva a disparar el fetch (filtros codependientes).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [districts]);

  useEffect(() => {
    if (!isMounted.current) return;
    fetchDistricts(organizationIds.length ? organizationIds : undefined, { country })
      .then((data) => { setDistrictOptions(data); setDistrictsError(false); })
      .catch(() => setDistrictsError(true));
    // Solo cuando cambian las organizaciones: el cambio de país ya lo cubre
    // el efecto de arriba (incluirlo acá duplicaría el fetch).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationIds]);

  const [events, setEvents] = useState<CiudadVivaEvent[]>(initialEvents);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [total, setTotal] = useState(initialTotal);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const hasMore = page < totalPages;

  const buildParams = useCallback((pageNum: number) => {
    return {
      ...(cat ? { category: cat } : {}),
      ...(when !== "all" ? { dateFilter: when as "today" | "weekend" } : {}),
      ...(price !== "all" ? { priceFilter: price as "free" | "paid" | "unknown" } : {}),
      ...(districts.length ? { districts } : {}),
      ...(organizationIds.length ? { organizationIds } : {}),
      ...(debouncedQ ? { search: debouncedQ } : {}),
      country,
      page: pageNum,
      limit: PAGE_SIZE,
      lang,
    };
  }, [cat, when, price, districts, organizationIds, debouncedQ, country, lang]);

  // El server ya trajo la primera página de Chile SIN filtros (para que Google vea
  // contenido real en el HTML inicial de /eventos, la ruta con más volumen
  // de búsqueda). Si el primer fetch post-bootstrap resulta ser justo ese
  // mismo caso (sin filtros activos, ni desde la URL ni por interacción),
  // reusamos esos datos en vez de repetir la misma llamada al toque.
  const skipNextFetch = useRef(true);

  useEffect(() => {
    if (!bootstrapDone) return; // wait for URL params to hydrate into store before first fetch
    if (!countryLoaded) return; // y a saber el país (el server trajo Chile)
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      const isDefaultFilters =
        !cat && when === "all" && price === "all" &&
        districts.length === 0 && organizationIds.length === 0 && !debouncedQ &&
        country === DEFAULT_COUNTRY;
      if (isDefaultFilters) return;
    }
    setLoading(true);
    setPage(1);
    fetchEvents(buildParams(1))
      .then((res) => {
        setEvents(res.data);
        setTotalPages(res.meta.totalPages);
        setTotal(res.meta.total);
      })
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [bootstrapDone, countryLoaded, cat, when, price, districts, organizationIds, debouncedQ, country, buildParams]);

  async function loadMore() {
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const res = await fetchEvents(buildParams(nextPage));
      setEvents((prev) => [...prev, ...res.data]);
      setPage(nextPage);
      setTotalPages(res.meta.totalPages);
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Sticky search */}
      <div ref={searchBarRef} className="sticky top-16 z-30 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="container-page flex items-center gap-3 py-4">
          <div className="flex flex-1 items-center gap-2 rounded-full border border-border bg-surface px-4 py-2.5">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t.searchPlaceholder}
              className="w-full border-0 bg-transparent p-0 shadow-none text-sm focus-visible:ring-0 placeholder:text-muted-foreground"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setMobileOpen(true)}
            className="rounded-full md:hidden"
          >
            <SlidersHorizontal className="h-4 w-4" /> {t.filters}
          </Button>
        </div>

        {/* Category chips */}
        <div className="container-page flex gap-2 overflow-x-auto pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <Chip active={!cat} onClick={() => setCat(null)}>{t.all}</Chip>
          {categories.map((c) => (
            <Chip key={c.name} active={cat === c.name} onClick={() => setCat(cat === c.name ? null : c.name)}>
              <span className="mr-1.5">{c.emoji}</span>{tEvents.categories[c.name]}
            </Chip>
          ))}
        </div>
      </div>

      <div className="container-page grid gap-10 py-10 md:grid-cols-[260px_1fr]">
        {/* Sidebar */}
        <aside className="hidden md:block md:self-start md:sticky" style={{ top: sidebarTop }}>
          <SidebarFilters
            when={when} setWhen={setWhen}
            price={price} setPrice={setPrice}
            districts={districts} setDistricts={setDistricts}
            organizationIds={organizationIds} setOrganizationIds={setOrganizationIds}
            districtOptions={districtOptions}
            districtsError={districtsError}
            organizations={organizations}
            organizationsError={organizationsError}
            t={t}
          />
        </aside>

        {/* Results */}
        <div>
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">{t.title}</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {loading ? t.loading : t.results(total)}
              </p>
            </div>
            <Link
              href="/registro"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-4 py-2 text-sm font-medium text-primary transition hover:bg-primary/15"
            >
              {t.organizerCta} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-72 animate-pulse rounded-2xl bg-surface" />
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-surface p-16 text-center">
              <p className="text-foreground">{t.noResults}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t.noResultsHint}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {events.map((e, i) => (
                  <Fragment key={e.slug}>
                    <EventCard event={e} position={i + 1} />
                    {/* Invitación al juego tras la 6ª tarjeta (o la última). */}
                    {i === Math.min(GAME_INVITE_AFTER, events.length) - 1 && (
                      <DailyWordInvite source="events" className="col-span-full" />
                    )}
                  </Fragment>
                ))}
              </div>

              {hasMore && (
                <div className="mt-10 flex justify-center">
                  <Button
                    variant="outline"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="h-11 rounded-full px-6"
                  >
                    {loadingMore
                      ? <><Loader2 className="h-4 w-4 animate-spin" /> {t.loading}</>
                      : t.loadMore}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile bottom sheet */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-foreground/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-3xl bg-surface p-6 shadow-lift">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">{t.filters}</h3>
              <button onClick={() => setMobileOpen(false)} className="rounded-full p-1.5 hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>
            <SidebarFilters
              when={when} setWhen={setWhen}
              price={price} setPrice={setPrice}
              districts={districts} setDistricts={setDistricts}
              organizationIds={organizationIds} setOrganizationIds={setOrganizationIds}
              districtOptions={districtOptions}
              districtsError={districtsError}
              organizations={organizations}
              organizationsError={organizationsError}
              t={t}
            />
            <Button
              onClick={() => setMobileOpen(false)}
              className="mt-6 h-12 w-full rounded-full"
            >
              {t.seeResults}
            </Button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

function Chip({ children, active, onClick }: { children: React.ReactNode; active?: boolean; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-medium transition ${
        active
          ? "border-foreground bg-foreground text-background"
          : "border-border bg-surface text-foreground hover:border-foreground/30"
      }`}
    >
      {children}
    </button>
  );
}

function SidebarFilters({
  when, setWhen, price, setPrice, districts, setDistricts,
  organizationIds, setOrganizationIds, districtOptions, districtsError, organizations, organizationsError, t,
}: {
  when: string;
  setWhen: (v: "all" | "today" | "weekend") => void;
  price: string;
  setPrice: (v: "all" | "free" | "paid" | "unknown") => void;
  districts: string[];
  setDistricts: (v: string[]) => void;
  organizationIds: string[];
  setOrganizationIds: (v: string[]) => void;
  districtOptions: DistrictOption[];
  districtsError: boolean;
  organizations: { _id: string; name: string; eventCount: number }[];
  organizationsError: boolean;
  t: T;
}) {
  const lang = useLocale();
  const locationOptions = districtOptions
    .filter((d) => !!d.name)
    .map((d) => ({
      label: d.count > 0 ? `${d.name} (${d.count})` : d.name,
      value: d.name,
      group: countryName(d.country ?? "CL", lang),
    }));

  const organizationOptions = organizations.map((o) => ({
    label: `${o.name} (${o.eventCount})`,
    value: o._id,
  }));

  return (
    <div className="space-y-7 text-sm">
      <FilterGroup label={t.district} highlight>
        {districtsError ? (
          <p className="text-xs text-red-500">{t.loadError}</p>
        ) : (
          <MultiSelect
            options={locationOptions}
            selected={districts}
            onChange={setDistricts}
            placeholder={t.allDistricts}
            searchPlaceholder={t.searchDistrict}
            t={t}
          />
        )}
      </FilterGroup>

      {(organizationOptions.length > 0 || organizationsError) && (
        <FilterGroup label={t.organization}>
          {organizationsError ? (
            <p className="text-xs text-red-500">{t.loadError}</p>
          ) : (
            <MultiSelect
              options={organizationOptions}
              selected={organizationIds}
              onChange={setOrganizationIds}
              placeholder={t.allOrganizations}
              searchPlaceholder={t.searchOrganization}
              t={t}
            />
          )}
        </FilterGroup>
      )}

      <FilterGroup label={t.when}>
        {[
          { v: "all", l: t.anytime },
          { v: "today", l: t.today },
          { v: "weekend", l: t.weekend },
        ].map((o) => (
          <Radio key={o.v} checked={when === o.v} onChange={() => setWhen(o.v as "all" | "today" | "weekend")} label={o.l} />
        ))}
      </FilterGroup>

      <FilterGroup label={t.price}>
        {[
          { v: "all", l: t.allPrices },
          { v: "free", l: t.free },
          { v: "paid", l: t.paid },
          { v: "unknown", l: t.unspecified },
        ].map((o) => (
          <Radio key={o.v} checked={price === o.v} onChange={() => setPrice(o.v as "all" | "free" | "paid" | "unknown")} label={o.l} />
        ))}
      </FilterGroup>
    </div>
  );
}

function FilterGroup({ label, children, highlight }: { label: string; children: React.ReactNode; highlight?: boolean }) {
  return (
    <div className={highlight ? "rounded-2xl border border-primary/30 bg-primary/5 p-4" : ""}>
      <p className={`mb-3 text-xs font-semibold uppercase tracking-widest ${highlight ? "text-primary" : "text-muted-foreground"}`}>
        {label}
      </p>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Radio({ checked, onChange, label }: { checked: boolean; onChange: () => void; label: string }) {
  return (
    <label className="flex cursor-pointer items-center gap-2.5">
      <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${checked ? "border-primary" : "border-border"}`}>
        {checked && <span className="h-2 w-2 rounded-full bg-primary" />}
      </span>
      <input type="radio" checked={checked} onChange={onChange} className="sr-only" />
      <span>{label}</span>
    </label>
  );
}

function MultiSelect({
  options,
  selected,
  onChange,
  placeholder,
  searchPlaceholder,
  t,
}: {
  // `group` (opcional) agrupa las opciones bajo un encabezado, ej. el país
  // en el filtro "Ubicación". Con un solo grupo no se muestran encabezados.
  options: { label: string; value: string; group?: string }[];
  selected: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
  searchPlaceholder?: string;
  t: T;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  function toggle(value: string) {
    onChange(selected.includes(value) ? selected.filter((x) => x !== value) : [...selected, value]);
  }

  const visible = options.filter((o) => o.label?.toLowerCase().includes(search.toLowerCase()));
  const showGroups = new Set(options.map((o) => o.group)).size > 1;
  const label = selected.length === 0
    ? placeholder
    : selected.length === 1
    ? (options.find((o) => o.value === selected[0])?.label ?? selected[0])
    : t.selected(selected.length);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 rounded-xl border border-border bg-surface px-3 py-2.5 text-sm transition hover:border-foreground/30"
      >
        <span className={selected.length > 0 ? "text-foreground font-medium" : "text-muted-foreground"}>
          {label}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-xl border border-border bg-surface shadow-card">
          <div className="border-b border-border px-3 py-2">
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder ?? t.search}
              className="w-full border-0 bg-transparent p-0 shadow-none text-sm focus-visible:ring-0 placeholder:text-muted-foreground"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {visible.length === 0 ? (
              <p className="px-3 py-3 text-sm text-muted-foreground">{t.noMatches}</p>
            ) : (
              visible.map((opt, i) => (
                <Fragment key={opt.value}>
                {showGroups && opt.group && opt.group !== visible[i - 1]?.group && (
                  <p className="bg-muted/50 px-3 pb-1 pt-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {opt.group}
                  </p>
                )}
                <div
                  role="checkbox"
                  aria-checked={selected.includes(opt.value)}
                  tabIndex={0}
                  onClick={() => toggle(opt.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggle(opt.value);
                    }
                  }}
                  className="flex cursor-pointer items-center gap-2.5 px-3 py-2.5 text-sm hover:bg-muted"
                >
                  <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition ${selected.includes(opt.value) ? "border-primary bg-primary" : "border-border"}`}>
                    {selected.includes(opt.value) && <span className="text-[9px] font-bold text-primary-foreground">✓</span>}
                  </span>
                  <span>{opt.label}</span>
                </div>
                </Fragment>
              ))
            )}
          </div>
          <div className="border-t border-border px-3 py-2">
            <button
              onClick={() => { onChange([]); setOpen(false); }}
              disabled={selected.length === 0}
              className="text-xs text-muted-foreground hover:text-foreground disabled:invisible"
            >
              {t.clearSelection}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
